import type { ReactElement, SVGProps } from "react";

// Customer icon set — the founder's outline iconography (24px grid, 1.7px stroke, round
// caps/joins), mirrored from the shared design system (apps/admin/src/ui/icons). A focused
// subset for the customer surfaces + the left-nav; rendered inline with
// `stroke: currentColor` so tone comes from context. Presentational only.
export type IconName =
  | "home"
  | "vault"
  | "household"
  | "member"
  | "calendar"
  | "protection"
  | "suggestion"
  | "document"
  | "conversation"
  | "vehicle"
  | "dwelling"
  | "menu"
  | "close"
  | "plus"
  | "search"
  | "filter"
  | "phone"
  | "logout"
  | "star"
  | "check-circle"
  | "chevron-right"
  | "arrow-right";

const PATHS: Record<IconName, ReactElement> = {
  home: (
    <>
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <path d="M9 22V12h6v10" />
    </>
  ),
  vault: (
    <>
      <path d="M5 11h14a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2z" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </>
  ),
  household: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <path d="M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  member: (
    <>
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <path d="M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
    </>
  ),
  calendar: (
    <>
      <path d="M8 2v4" />
      <path d="M16 2v4" />
      <path d="M3 10h18" />
      <path d="M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
    </>
  ),
  protection: (
    <>
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  suggestion: (
    <>
      <path d="M9.94 14.94A2 2 0 0 0 8.5 13.5l-5.6-1.44a.5.5 0 0 1 0-.96L8.5 9.66a2 2 0 0 0 1.44-1.44l1.44-5.6a.5.5 0 0 1 .96 0l1.44 5.6a2 2 0 0 0 1.44 1.44l5.6 1.44a.5.5 0 0 1 0 .96l-5.6 1.44a2 2 0 0 0-1.44 1.44l-1.44 5.6a.5.5 0 0 1-.96 0z" />
      <path d="M19 3v4" />
      <path d="M21 5h-4" />
    </>
  ),
  document: (
    <>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z" />
      <path d="M14 2v5h5" />
      <path d="M16 13H8" />
      <path d="M16 17H8" />
    </>
  ),
  conversation: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  vehicle: (
    <>
      <path d="M5 17H3v-5.5a2 2 0 0 1 .28-1.02l1.9-3.16A2 2 0 0 1 6.9 6.3h10.2a2 2 0 0 1 1.72.98l1.9 3.2a2 2 0 0 1 .28 1.02V17h-2" />
      <path d="M8 17h8" />
      <circle cx="6.5" cy="17" r="1.7" />
      <circle cx="17.5" cy="17" r="1.7" />
    </>
  ),
  dwelling: (
    <>
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <path d="M9 22V12h6v10" />
    </>
  ),
  menu: (
    <>
      <path d="M4 6h16" />
      <path d="M4 12h16" />
      <path d="M4 18h16" />
    </>
  ),
  close: (
    <>
      <path d="M18 6 6 18" />
      <path d="M6 6l12 12" />
    </>
  ),
  plus: (
    <>
      <path d="M5 12h14" />
      <path d="M12 5v14" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.34-4.34" />
    </>
  ),
  filter: (
    <>
      <path d="M3 6h18" />
      <path d="M7 12h10" />
      <path d="M10 18h4" />
    </>
  ),
  phone: (
    <path d="M13.83 16.57a1 1 0 0 0 1.21-.3l.36-.47A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.47.35a1 1 0 0 0-.29 1.23 14 14 0 0 0 6.39 6.39z" />
  ),
  logout: (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
    </>
  ),
  star: (
    <path d="M11.5 3.6a.6.6 0 0 1 1 0l2.4 5 5.3.8a.6.6 0 0 1 .3 1l-3.8 3.7.9 5.3a.6.6 0 0 1-.86.63L12 18.3l-4.7 2.5a.6.6 0 0 1-.87-.63l.9-5.3-3.8-3.7a.6.6 0 0 1 .3-1l5.3-.8z" />
  ),
  "check-circle": (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  "chevron-right": <path d="m9 18 6-6-6-6" />,
  "arrow-right": (
    <>
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </>
  ),
};

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, "name"> {
  name: IconName;
  /** px size (defaults to the 24px grid). */
  size?: number;
  /** Stroke weight — 1.7 is the sheet default. */
  strokeWidth?: number;
}

export function Icon({ name, size = 24, strokeWidth = 1.7, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}
